import { Clock3, RefreshCw } from 'lucide-react';
import PropTypes from 'prop-types';
import StateBadge from './StateBadge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { formatDate } from '../lib/utils';
import { editableJobFilters, categoryLabel } from '../lib/tyreReports';

const statusLabel = (status) => ({ queued: 'Queued', running: 'Running', completed: 'Completed', failed: 'Failed' }[status] || status);
const dateValue = (filters, camel, snake) => filters?.[camel] || filters?.[snake] || '';

const periodLabel = (job) => {
  const from = dateValue(job.filters, 'dateFrom', 'date_from');
  const to = dateValue(job.filters, 'dateTo', 'date_to');
  if (!from && !to) return 'All time';
  return `${from ? formatDate(`${from}T00:00:00`) : 'Beginning'} – ${to ? formatDate(`${to}T00:00:00`) : 'Today'}`;
};

const GeneratedReportsPanel = ({ jobs, loading, error, loadingResultId, onRefresh, onView, onAnalyze }) => (
  <Card className="border-0 shadow-none">
    <CardHeader className="flex-row items-start justify-between gap-4 space-y-0 p-5 pb-3">
      <div>
        <CardTitle>Report history</CardTitle>
      </div>
      <Button type="button" size="sm" variant="ghost" disabled={loading} onClick={onRefresh}>
        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
      </Button>
    </CardHeader>
    <CardContent className="px-5 pb-5">
      {error && <p role="alert" className="mb-4 text-sm text-red-700">{error}</p>}
      {loading && <p role="status" className="mb-3 text-sm text-slate-500">Loading generated reports…</p>}
      {!jobs.length && !loading && !error ? (
        <div className="flex min-h-[88px] items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/70 px-4 py-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm"><Clock3 className="h-4 w-4" /></span>
          <div><h3 className="text-sm font-bold text-slate-900">No generated reports yet</h3><p className="mt-0.5 text-xs leading-5 text-slate-500">Choose an open-ended or longer-than-six-month range to generate one.</p></div>
        </div>
      ) : (
        <ul className="divide-y divide-slate-200" aria-label="Generated reports">
          {jobs.map((job) => (
            <li key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-4 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-slate-900">{periodLabel(job)}</p>
                  <StateBadge value={job.status} label={statusLabel(job.status)} />
                </div>
                <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                  <Clock3 className="h-3.5 w-3.5" /> Requested {job.createdAt ? formatDate(job.createdAt) : 'recently'}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {editableJobFilters(job.filters).vehicleId ? `Vehicle ${editableJobFilters(job.filters).vehicleId}` : 'All vehicles'} · {job.filters?.status && job.filters.status !== 'all' ? job.filters.status.replace(/_/g, ' ') : 'All statuses'} · {job.filters?.category && job.filters.category !== 'all' ? categoryLabel(job.filters.category) : 'All categories'}
                </p>
                {job.status === 'failed' && <p className="mt-1 text-sm text-red-700">{job.error || 'Report generation failed. Generate it again from the filters above.'}</p>}
                {(job.status === 'queued' || job.status === 'running') && <p className="mt-1 text-xs text-slate-500">You can leave this page; processing will continue.</p>}
              </div>
              {job.status === 'completed' && (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={loadingResultId != null} onClick={() => onView(job)}>
                    {loadingResultId === job.id ? 'Opening…' : 'Open report'}
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={loadingResultId != null} onClick={() => onAnalyze(job)}>Analyze</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

GeneratedReportsPanel.propTypes = {
  jobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  loadingResultId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRefresh: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onAnalyze: PropTypes.func.isRequired,
};

export default GeneratedReportsPanel;
